import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dataset, DatasetSummary, DatasetQuestion } from '../types/dashboard';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-sql';
import 'prismjs/themes/prism-tomorrow.css';

// Use relative URLs so they work in both dev (via Vite proxy) and production
const API_BASE = '';

interface Agent {
  name: string;
  path: string;
  display_name: string;
}

interface Model {
  name: string;
  display_name: string;
  description: string;
  input_token_limit?: number;
  output_token_limit?: number;
}

interface ConfigurationPanelProps {
  onNavigateToDashboard?: () => void;
}

export default function ConfigurationPanel({ onNavigateToDashboard }: ConfigurationPanelProps) {
  const [datasets, setDatasets] = useState<DatasetSummary[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [editedDataset, setEditedDataset] = useState<Dataset | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [view, setView] = useState<'start-eval' | 'list' | 'edit' | 'import'>('start-eval');
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'dataset' | 'question', id: string | number } | null>(null);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [showArchitecture, setShowArchitecture] = useState(false);

  // Evaluation form state
  const [agents, setAgents] = useState<Agent[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [selectedDatasetVersion, setSelectedDatasetVersion] = useState<string>('');
  const [evalLimit, setEvalLimit] = useState<number | ''>('');
  const [evalRepeat, setEvalRepeat] = useState<number>(1);
  // Parallel workers now managed by Cloud Tasks (max 10 concurrent)
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [isEvaluationRunning, setIsEvaluationRunning] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loadingModels, setLoadingModels] = useState(true);

  // Validation boundaries
  const LIMITS = {
    REPEAT_MIN: 1,
    REPEAT_MAX: 10,
    WORKERS_MIN: 1,
    WORKERS_MAX: 10,
    LIMIT_MIN: 1,
  };

  // Get max questions for current dataset
  const getMaxQuestions = () => {
    const ds = datasets.find(d => d.version === selectedDatasetVersion);
    return ds?.total_questions || 100;
  };

  // Clamp helper
  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

  // Load available datasets
  useEffect(() => {
    loadDatasets();
    loadAgents();
    loadModels();
    checkEvaluationStatus();

    // Poll for status every 5 seconds
    const interval = setInterval(checkEvaluationStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const checkEvaluationStatus = async () => {
    try {
      const response = await fetch('/api/evaluation/status');
      const data = await response.json();

      // Only show as running if is_active flag is true
      if (data.is_active && data.run_id) {
        setActiveRunId(data.run_id);
        setIsEvaluationRunning(true);
      } else {
        setActiveRunId(null);
        setIsEvaluationRunning(false);
      }
    } catch (err) {
      console.error('Failed to check evaluation status:', err);
    }
  };

  const loadDatasets = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/evaluation/datasets`);
      const data = await response.json();
      setDatasets(data.datasets || []);
      // Auto-select first dataset
      if (data.datasets && data.datasets.length > 0 && !selectedDatasetVersion) {
        setSelectedDatasetVersion(data.datasets[0].version);
      }
    } catch (err) {
      console.error('Failed to load datasets:', err);
      setError('Failed to load datasets');
    }
  };

  const loadAgents = async () => {
    setLoadingAgents(true);
    try {
      const response = await fetch(`${API_BASE}/api/evaluation/agents`);
      const data = await response.json();
      setAgents(data.agents || []);
      // Auto-select first agent
      if (data.agents && data.agents.length > 0 && !selectedAgent) {
        setSelectedAgent(data.agents[0].path);
      }
    } catch (err) {
      console.error('Failed to load agents:', err);
      setError('Failed to load agents');
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadModels = async () => {
    setLoadingModels(true);
    try {
      const response = await fetch(`${API_BASE}/api/evaluation/models`);
      const data = await response.json();
      setModels(data.models || []);
      // Auto-select Gemini 3 (or flash, or first model)
      if (data.models && data.models.length > 0 && !selectedModel) {
        // Prefer gemini-3, then flash, then first model
        const gemini3Model = data.models.find((m: Model) =>
          m.name.toLowerCase().includes('gemini-3')
        );
        const flashModel = data.models.find((m: Model) =>
          m.name.toLowerCase().includes('flash')
        );
        setSelectedModel(gemini3Model ? gemini3Model.name : (flashModel ? flashModel.name : data.models[0].name));
      }
    } catch (err) {
      console.error('Failed to load models:', err);
      // Don't set error, just use default model
    } finally {
      setLoadingModels(false);
    }
  };

  const startEvaluation = async () => {
    if (!selectedAgent || !selectedDatasetVersion) {
      setError('Please select an agent and dataset');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/evaluation/run-evaluation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_path: selectedAgent,
          dataset_version: selectedDatasetVersion,
          model: selectedModel,
          limit: evalLimit || null,
          repeat: evalRepeat,
          parallel_workers: 10  // Managed by Cloud Tasks
        })
      });

      const result = await response.json();

      if (result.error) {
        setError(result.error);
        return;
      }

      setSuccessMessage(`Evaluation started! Run ID: ${result.run_id}`);
      setActiveRunId(result.run_id);
      setIsEvaluationRunning(true);

      // Redirect to live evaluation view after 1 second
      setTimeout(() => {
        if (onNavigateToDashboard) {
          onNavigateToDashboard();
        }
        // Navigate to dashboard (live mode will be auto-detected)
        window.location.href = `/dashboard`;
      }, 1000);
    } catch (err: any) {
      console.error('Failed to start evaluation:', err);
      setError(`Failed to start evaluation: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadDataset = async (version: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/evaluation/datasets/${version}`);
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        return;
      }

      setSelectedDataset(data);
      setEditedDataset(JSON.parse(JSON.stringify(data))); // Deep copy
      setView('edit');
    } catch (err) {
      console.error('Failed to load dataset:', err);
      setError('Failed to load dataset');
    } finally {
      setLoading(false);
    }
  };

  const saveDataset = () => {
    if (!editedDataset) return;

    // Check if we're overwriting an existing dataset
    const isOverwriting = datasets.some(ds => ds.version === editedDataset.version);

    if (isOverwriting) {
      setShowSaveConfirmModal(true);
    } else {
      performSave();
    }
  };

  const performSave = async () => {
    if (!editedDataset) return;

    setShowSaveConfirmModal(false);
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Check if this is an existing dataset (update) or new dataset (create)
      const isExisting = datasets.some(ds => ds.version === editedDataset.version);

      let response: Response;
      if (isExisting) {
        // Use PUT for existing datasets
        response = await fetch(`${API_BASE}/api/evaluation/datasets/${editedDataset.version}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editedDataset)
        });
      } else {
        // Use POST /import for new datasets
        response = await fetch(`${API_BASE}/api/evaluation/datasets/import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editedDataset)
        });
      }

      const result = await response.json();

      if (result.error) {
        setError(result.error);
        return;
      }

      setSuccessMessage(`Dataset ${editedDataset.version} saved successfully!`);
      loadDatasets();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save dataset:', err);
      setError('Failed to save dataset');
    } finally {
      setLoading(false);
    }
  };

  const importDataset = async (jsonContent: string) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const parsed = JSON.parse(jsonContent);

      const response = await fetch(`${API_BASE}/api/evaluation/datasets/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed)
      });

      const result = await response.json();

      if (result.error) {
        setError(result.error);
        return;
      }

      setSuccessMessage(`Dataset ${result.version} imported successfully!`);
      loadDatasets();
      setView('list');

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to import dataset:', err);
      setError(`Failed to import dataset: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteDataset = async (version: string) => {
    setDeleteTarget({ type: 'dataset', id: version });
    setShowDeleteModal(true);
  };

  const performDelete = async () => {
    if (!deleteTarget) return;

    setShowDeleteModal(false);
    setLoading(true);
    setError(null);

    try {
      if (deleteTarget.type === 'dataset') {
        const response = await fetch(`${API_BASE}/api/evaluation/datasets/${deleteTarget.id}`, {
          method: 'DELETE'
        });

        const result = await response.json();

        if (result.error) {
          setError(result.error);
          return;
        }

        setSuccessMessage(`Dataset ${deleteTarget.id} deleted successfully!`);
        loadDatasets();
        if (selectedDataset?.version === deleteTarget.id) {
          setSelectedDataset(null);
          setEditedDataset(null);
          setView('list');
        }
      } else if (deleteTarget.type === 'question') {
        // Handle question deletion
        if (!editedDataset) return;
        const updated = { ...editedDataset };
        updated.questions.splice(deleteTarget.id as number, 1);
        updated.total_questions = updated.questions.length;
        setEditedDataset(updated);

        if (editingQuestionIndex === deleteTarget.id) {
          setEditingQuestionIndex(null);
        }
        setSuccessMessage('Question removed successfully!');
      }

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to delete:', err);
      setError('Failed to delete');
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  const updateQuestion = (index: number, field: keyof DatasetQuestion, value: any) => {
    if (!editedDataset) return;

    const updated = { ...editedDataset };
    updated.questions[index] = {
      ...updated.questions[index],
      [field]: value
    };
    setEditedDataset(updated);
  };

  const addQuestion = () => {
    if (!editedDataset) return;

    const newQuestion: DatasetQuestion = {
      id: `q${String(editedDataset.questions.length + 1).padStart(3, '0')}`,
      domain: '',
      question: '',
      difficulty: 'medium',
      category: '',
      expected_tables: [],
      reference_sql: '',
      explanation: '',
      human_readable_answer: ''
    };

    const updated = { ...editedDataset };
    updated.questions.push(newQuestion);
    updated.total_questions = updated.questions.length;
    setEditedDataset(updated);
    setEditingQuestionIndex(updated.questions.length - 1);
  };

  const removeQuestion = (index: number) => {
    if (!editedDataset) return;
    setDeleteTarget({ type: 'question', id: index });
    setShowDeleteModal(true);
  };

  const createNewDataset = () => {
    // Generate a suggested version number based on existing datasets
    const existingVersions = datasets.map(ds => ds.version);
    let suggestedVersion = '1.0';

    // Try to find the next version number
    const versionNumbers = existingVersions
      .map(v => parseFloat(v))
      .filter(n => !isNaN(n));
    if (versionNumbers.length > 0) {
      const maxVersion = Math.max(...versionNumbers);
      suggestedVersion = (maxVersion + 1).toFixed(1);
    }

    const newDataset: Dataset = {
      version: suggestedVersion,
      name: 'New Dataset',
      description: '',
      created_date: new Date().toISOString().split('T')[0],
      domains: [],
      total_questions: 0,
      questions: []
    };

    setSelectedDataset(null);
    setEditedDataset(newDataset);
    setView('edit');
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-primary via-orange-400 to-amber-300 bg-clip-text text-transparent mb-2">Configuration</h1>
        <p className="text-muted-foreground">Start evaluations and manage datasets</p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-destructive/20 border border-destructive/40 text-destructive rounded-xl backdrop-blur-sm">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-success/20 border border-success/40 text-success rounded-xl backdrop-blur-sm">
          {successMessage}
        </div>
      )}

      {/* Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setView('start-eval')}
          className={`px-4 py-2 rounded-lg transition-all ${
            view === 'start-eval'
              ? 'bg-primary text-primary-foreground shadow-lg glow'
              : 'bg-card/50 text-muted-foreground hover:text-foreground hover:bg-card border border-primary/20'
          }`}
        >
          Start Evaluation
        </button>
        <button
          onClick={() => setView('list')}
          className={`px-4 py-2 rounded-lg transition-all ${
            view === 'list'
              ? 'bg-primary text-primary-foreground shadow-lg glow'
              : 'bg-card/50 text-muted-foreground hover:text-foreground hover:bg-card border border-primary/20'
          }`}
        >
          Dataset List
        </button>
        <button
          onClick={() => setView('import')}
          className={`px-4 py-2 rounded-lg transition-all ${
            view === 'import'
              ? 'bg-primary text-primary-foreground shadow-lg glow'
              : 'bg-card/50 text-muted-foreground hover:text-foreground hover:bg-card border border-primary/20'
          }`}
        >
          Import JSON
        </button>
        {selectedDataset && (
          <button
            onClick={() => setView('edit')}
            className={`px-4 py-2 rounded-lg transition-all ${
              view === 'edit'
                ? 'bg-primary text-primary-foreground shadow-lg glow'
                : 'bg-card/50 text-muted-foreground hover:text-foreground hover:bg-card border border-primary/20'
            }`}
          >
            Edit: {selectedDataset.version}
          </button>
        )}
      </div>

      {/* Start Evaluation View */}
      {view === 'start-eval' && (
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-orange-400 bg-clip-text text-transparent mb-4">Start New Evaluation</h2>
          <div className="rounded-xl border border-primary/20 bg-card/50 p-6 backdrop-blur-sm">
            <div className="space-y-6">
              {/* Agent Selection */}
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">
                  Select Agent
                </label>
                {loadingAgents ? (
                  <div className="w-full p-3 border rounded-lg bg-background border-primary/20 flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    Loading agents...
                  </div>
                ) : (
                  <select
                    value={selectedAgent}
                    onChange={(e) => setSelectedAgent(e.target.value)}
                    className="w-full p-3 border rounded-lg bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    {agents.map((agent) => (
                      <option key={agent.path} value={agent.path}>
                        {agent.display_name} ({agent.path})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Model Selection */}
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">
                  Gemini Model
                </label>
                {loadingModels ? (
                  <div className="w-full p-3 border rounded-lg bg-background border-primary/20 flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    Loading models...
                  </div>
                ) : (
                  <select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="w-full p-3 border rounded-lg bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    {models.map((model) => {
                      // Trim the model name for display: "publishers/google/models/gemini-2.5-flash" -> "gemini-2.5-flash"
                      const displayName = model.display_name ||
                        (model.name.includes('/') ? model.name.split('/').pop() : model.name);

                      return (
                        <option
                          key={model.name}
                          value={model.name}
                        >
                          {displayName}
                          {model.input_token_limit && ` (${(model.input_token_limit / 1000000).toFixed(1)}M tokens)`}
                        </option>
                      );
                    })}
                  </select>
                )}
                {!loadingModels && models.find(m => m.name === selectedModel)?.description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {models.find(m => m.name === selectedModel)?.description}
                  </p>
                )}
              </div>

              {/* Dataset Selection */}
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">
                  Select Dataset
                </label>
                <select
                  value={selectedDatasetVersion}
                  onChange={(e) => setSelectedDatasetVersion(e.target.value)}
                  className="w-full p-3 border rounded-lg bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                >
                  {datasets.map((ds) => (
                    <option key={ds.version} value={ds.version}>
                      {ds.version} - {ds.name} ({ds.total_questions} questions)
                    </option>
                  ))}
                </select>
              </div>

              {/* Limit Questions */}
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">
                  Limit Questions (Optional)
                </label>
                <input
                  type="number"
                  value={evalLimit}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setEvalLimit('');
                    } else {
                      const num = parseInt(val);
                      if (!isNaN(num)) {
                        setEvalLimit(clamp(num, LIMITS.LIMIT_MIN, getMaxQuestions()));
                      }
                    }
                  }}
                  min={LIMITS.LIMIT_MIN}
                  max={getMaxQuestions()}
                  placeholder="Leave empty for all questions"
                  className="w-full p-3 border rounded-lg bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Limit the number of questions to evaluate (1-{getMaxQuestions()}, or empty for all)
                </p>
              </div>

              {/* Repeat Count */}
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">
                  Repeat Count
                </label>
                <input
                  type="number"
                  value={evalRepeat}
                  onChange={(e) => {
                    const num = parseInt(e.target.value);
                    if (!isNaN(num)) {
                      setEvalRepeat(clamp(num, LIMITS.REPEAT_MIN, LIMITS.REPEAT_MAX));
                    }
                  }}
                  min={LIMITS.REPEAT_MIN}
                  max={LIMITS.REPEAT_MAX}
                  className="w-full p-3 border rounded-lg bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Number of times to repeat each question ({LIMITS.REPEAT_MIN}-{LIMITS.REPEAT_MAX})
                </p>
              </div>

              {/* Running Status Banner */}
              {isEvaluationRunning && activeRunId && (
                <div className="bg-warning/20 border-2 border-warning/60 rounded-xl p-4 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-warning animate-pulse" />
                      <div>
                        <p className="font-semibold text-warning">
                          Evaluation Running
                        </p>
                        <p className="text-sm text-warning/80">
                          Run ID: {activeRunId}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onNavigateToDashboard}
                      className="px-4 py-2 bg-warning hover:bg-warning/80 text-warning-foreground rounded-lg font-medium transition-colors"
                    >
                      View in Dashboard →
                    </button>
                  </div>
                </div>
              )}

              {/* Start Button */}
              <div className="pt-4">
                <button
                  onClick={startEvaluation}
                  disabled={loading || !selectedAgent || !selectedDatasetVersion || isEvaluationRunning}
                  className="w-full px-6 py-4 bg-gradient-to-r from-success to-emerald-500 text-success-foreground rounded-xl hover:from-success/90 hover:to-emerald-500/90 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg shadow-lg hover:shadow-success/30 transition-all glow-strong"
                >
                  {loading ? 'Starting Evaluation...' : isEvaluationRunning ? 'Evaluation In Progress...' : 'Start Evaluation'}
                </button>
                {isEvaluationRunning && (
                  <p className="text-sm text-center text-warning mt-2">
                    Please wait for the current evaluation to complete before starting a new one
                  </p>
                )}
              </div>

              {/* Model Info Badge */}
              {selectedModel && models.find(m => m.name === selectedModel) && (() => {
                const currentModel = models.find(m => m.name === selectedModel);
                // Trim the model name for display
                const displayName = currentModel?.display_name ||
                  (selectedModel.includes('/') ? selectedModel.split('/').pop() : selectedModel);

                return (
                  <div className="bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/40 rounded-xl p-4 backdrop-blur-sm">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-purple-300 mb-1">
                          {displayName}
                        </h4>
                        <p className="text-sm text-purple-400">
                          {currentModel?.description || 'Google Gemini generative model'}
                        </p>
                        {currentModel?.input_token_limit && (
                          <div className="flex gap-4 mt-2 text-xs text-purple-400/80">
                            <span>
                              📥 Input: {(currentModel.input_token_limit / 1000000).toFixed(1)}M tokens
                            </span>
                            {currentModel?.output_token_limit && (
                              <span>
                                📤 Output: {(currentModel.output_token_limit / 1000).toFixed(0)}K tokens
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Info Box */}
              <div className="bg-blue-500/20 border border-blue-500/40 rounded-xl p-4 backdrop-blur-sm">
                <h3 className="font-semibold text-blue-300 mb-2">Quick Tips:</h3>
                <ul className="text-sm text-blue-400 space-y-1">
                  <li>• Use limit=3 for quick testing during development</li>
                  <li>• Increase repeat count (3-5) for reliability testing (max {LIMITS.REPEAT_MAX})</li>
                  <li>• Start with 5 workers, max {LIMITS.WORKERS_MAX} allowed</li>
                  <li>• View results in real-time on the Dashboard tab</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dataset List View */}
      {view === 'list' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-orange-400 bg-clip-text text-transparent">Available Datasets</h2>
            <button
              onClick={createNewDataset}
              className="px-4 py-2 bg-success text-success-foreground rounded-lg hover:bg-success/80 transition-colors flex items-center gap-2"
            >
              <span>+</span> Create New Dataset
            </button>
          </div>
          <div className="grid gap-4">
            {datasets.map((ds) => (
              <div key={ds.version} className="rounded-xl border border-primary/20 bg-card/50 p-4 backdrop-blur-sm hover:border-primary/40 transition-all">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-foreground">{ds.version} - {ds.name}</h3>
                    <p className="text-muted-foreground mt-1">{ds.description}</p>
                    <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                      <span>{ds.total_questions} questions</span>
                      {ds.domains && ds.domains.length > 0 && (
                        <span>Domains: {ds.domains.join(', ')}</span>
                      )}
                      {ds.created_date && (
                        <span>Created: {ds.created_date}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => loadDataset(ds.version)}
                      className="px-3 py-1 bg-primary text-primary-foreground rounded-lg hover:bg-primary/80 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteDataset(ds.version)}
                      className="px-3 py-1 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/80 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Import View */}
      {view === 'import' && (
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-orange-400 bg-clip-text text-transparent mb-4">Import Dataset from JSON</h2>
          <p className="text-muted-foreground mb-4">
            Paste your dataset JSON below. The JSON should include all required fields: version, name, description, questions, etc.
          </p>
          <textarea
            id="import-json"
            className="w-full h-96 p-4 border rounded-xl font-mono text-sm bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
            placeholder='{\n  "version": "6.0",\n  "name": "My Dataset",\n  ...\n}'
          />
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => {
                const textarea = document.getElementById('import-json') as HTMLTextAreaElement;
                importDataset(textarea.value);
              }}
              disabled={loading}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/80 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Importing...' : 'Import Dataset'}
            </button>
            <button
              onClick={() => setView('list')}
              className="px-4 py-2 bg-card/50 text-muted-foreground rounded-lg hover:text-foreground hover:bg-card border border-primary/20 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit View */}
      {view === 'edit' && editedDataset && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-orange-400 bg-clip-text text-transparent">Edit Dataset: {editedDataset.version}</h2>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  // Clean up questions to only include current fields
                  const cleanedDataset = {
                    ...editedDataset,
                    questions: editedDataset.questions.map(q => ({
                      id: q.id,
                      ...(q.domain && { domain: q.domain }),
                      question: q.question,
                      ...(q.question_en && { question_en: q.question_en }),
                      difficulty: q.difficulty,
                      ...(q.expected_tables && q.expected_tables.length > 0 && { expected_tables: q.expected_tables }),
                      reference_sql: q.reference_sql,
                      ...(q.human_readable_answer && { human_readable_answer: q.human_readable_answer }),
                      ...(q.explanation && { explanation: q.explanation })
                    }))
                  };

                  const json = JSON.stringify(cleanedDataset, null, 2);
                  const blob = new Blob([json], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `dataset_${editedDataset.version}.json`;
                  a.click();
                }}
                className="px-4 py-2 bg-card/50 text-muted-foreground rounded-lg hover:text-foreground hover:bg-card border border-primary/20 transition-colors"
              >
                Export JSON
              </button>
              <button
                onClick={saveDataset}
                disabled={loading}
                className="px-4 py-2 bg-success text-success-foreground rounded-lg hover:bg-success/80 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => {
                  setEditedDataset(JSON.parse(JSON.stringify(selectedDataset)));
                }}
                className="px-4 py-2 bg-card/50 text-muted-foreground rounded-lg hover:text-foreground hover:bg-card border border-primary/20 transition-colors"
              >
                Reset
              </button>
              <button
                onClick={() => {
                  setView('list');
                  setSelectedDataset(null);
                  setEditedDataset(null);
                }}
                className="px-4 py-2 bg-card/50 text-muted-foreground rounded-lg hover:text-foreground hover:bg-card border border-primary/20 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Metadata */}
          <div className="rounded-xl border border-primary/20 bg-card/50 p-4 backdrop-blur-sm mb-6">
            <h3 className="font-bold mb-3 text-foreground">Dataset Metadata</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-foreground">Version</label>
                <input
                  type="text"
                  value={editedDataset.version}
                  onChange={(e) => setEditedDataset({ ...editedDataset, version: e.target.value })}
                  className="w-full p-2 border rounded-lg bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-foreground">Name</label>
                <input
                  type="text"
                  value={editedDataset.name}
                  onChange={(e) => setEditedDataset({ ...editedDataset, name: e.target.value })}
                  className="w-full p-2 border rounded-lg bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1 text-foreground">Description</label>
                <textarea
                  value={editedDataset.description}
                  onChange={(e) => setEditedDataset({ ...editedDataset, description: e.target.value })}
                  className="w-full p-2 border rounded-lg bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-foreground">Domains (comma-separated)</label>
                <input
                  type="text"
                  value={(editedDataset.domains || []).join(', ')}
                  onChange={(e) => setEditedDataset({ ...editedDataset, domains: e.target.value.split(',').map(d => d.trim()).filter(d => d) })}
                  className="w-full p-2 border rounded-lg bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-foreground">Created Date</label>
                <input
                  type="date"
                  value={editedDataset.created_date || ''}
                  onChange={(e) => setEditedDataset({ ...editedDataset, created_date: e.target.value })}
                  className="w-full p-2 border rounded-lg bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>

            {/* Guidelines - Expandable */}
            <div className="mt-4 border-t border-primary/20 pt-4">
              <button
                onClick={() => setShowGuidelines(!showGuidelines)}
                className="flex items-center justify-between w-full text-left"
              >
                <span className="font-medium text-foreground">Guidelines</span>
                <span className="text-muted-foreground">
                  {showGuidelines ? '▼' : '▶'}
                </span>
              </button>
              {showGuidelines && (
                <textarea
                  value={JSON.stringify(editedDataset.guidelines || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setEditedDataset({ ...editedDataset, guidelines: parsed });
                    } catch (err) {
                      // Invalid JSON, don't update
                    }
                  }}
                  className="w-full p-2 border rounded-lg bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary font-mono text-sm mt-2"
                  rows={8}
                  placeholder='{"key": "value"}'
                />
              )}
            </div>

            {/* Layer 3 Architecture - Expandable */}
            <div className="mt-4 border-t border-primary/20 pt-4">
              <button
                onClick={() => setShowArchitecture(!showArchitecture)}
                className="flex items-center justify-between w-full text-left"
              >
                <span className="font-medium text-foreground">Layer 3 Architecture</span>
                <span className="text-muted-foreground">
                  {showArchitecture ? '▼' : '▶'}
                </span>
              </button>
              {showArchitecture && (
                <textarea
                  value={JSON.stringify(editedDataset.layer3_architecture || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setEditedDataset({ ...editedDataset, layer3_architecture: parsed });
                    } catch (err) {
                      // Invalid JSON, don't update
                    }
                  }}
                  className="w-full p-2 border rounded-lg bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary font-mono text-sm mt-2"
                  rows={8}
                  placeholder='{"key": "value"}'
                />
              )}
            </div>
          </div>

          {/* Questions */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-foreground">Questions ({editedDataset.questions.length})</h3>
              <div className="flex gap-2">
                <button
                  onClick={addQuestion}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/80 transition-colors"
                >
                  + Add Question
                </button>
                {editedDataset.questions.length > 0 && (
                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to delete all questions? This cannot be undone.')) {
                        setEditedDataset({
                          ...editedDataset,
                          questions: [],
                          total_questions: 0
                        });
                      }
                    }}
                    className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/80 transition-colors"
                  >
                    Delete All
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {editedDataset.questions.map((q, index) => (
                <div key={q.id} className="rounded-xl border border-primary/20 bg-card/50 p-4 backdrop-blur-sm">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-bold text-lg text-foreground">{q.id}</h4>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingQuestionIndex(editingQuestionIndex === index ? null : index)}
                        className="px-3 py-1 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors font-medium"
                      >
                        {editingQuestionIndex === index ? 'Collapse' : 'Expand'}
                      </button>
                      <button
                        onClick={() => removeQuestion(index)}
                        className="px-3 py-1 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/80 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  {editingQuestionIndex === index ? (
                    <div className="grid gap-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1 text-foreground">ID</label>
                          <input
                            type="text"
                            value={q.id}
                            onChange={(e) => updateQuestion(index, 'id', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1 text-foreground">Domain</label>
                          <input
                            type="text"
                            value={q.domain || ''}
                            onChange={(e) => updateQuestion(index, 'domain', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1 text-foreground">Difficulty</label>
                          <select
                            value={q.difficulty}
                            onChange={(e) => updateQuestion(index, 'difficulty', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                          >
                            <option value="easy">Easy</option>
                            <option value="medium">Medium</option>
                            <option value="hard">Hard</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1 text-foreground">Question</label>
                        <textarea
                          value={q.question}
                          onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                          className="w-full p-2 border rounded-lg text-sm bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                          rows={2}
                        />
                      </div>

                      {q.question_en !== undefined && (
                        <div>
                          <label className="block text-sm font-medium mb-1 text-foreground">Question (English)</label>
                          <textarea
                            value={q.question_en || ''}
                            onChange={(e) => updateQuestion(index, 'question_en', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                            rows={2}
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium mb-1 text-foreground">Expected Tables</label>
                        <div className="space-y-2">
                          {(q.expected_tables || []).map((table, tableIdx) => (
                            <div key={tableIdx} className="flex gap-2">
                              <input
                                type="text"
                                value={table}
                                onChange={(e) => {
                                  const newTables = [...(q.expected_tables || [])];
                                  newTables[tableIdx] = e.target.value;
                                  updateQuestion(index, 'expected_tables', newTables);
                                }}
                                className="flex-1 p-2 border rounded-lg text-sm bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                                placeholder="Table name"
                              />
                              <button
                                onClick={() => {
                                  const newTables = [...(q.expected_tables || [])];
                                  newTables.splice(tableIdx, 1);
                                  updateQuestion(index, 'expected_tables', newTables);
                                }}
                                className="px-3 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/80 text-sm transition-colors"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const newTables = [...(q.expected_tables || []), ''];
                              updateQuestion(index, 'expected_tables', newTables);
                            }}
                            className="px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/80 text-sm transition-colors"
                          >
                            + Add Table
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1 text-foreground">Reference SQL</label>
                        <div className="border border-primary/30 rounded-lg overflow-hidden">
                          <Editor
                            value={q.reference_sql}
                            onValueChange={(code) => updateQuestion(index, 'reference_sql', code)}
                            highlight={(code) => highlight(code, languages.sql, 'sql')}
                            padding={12}
                            tabSize={2}
                            insertSpaces={true}
                            style={{
                              fontFamily: 'Monaco, Menlo, "Courier New", monospace',
                              fontSize: 13,
                              lineHeight: 1.6,
                              backgroundColor: '#1e1e1e',
                              minHeight: '300px'
                            }}
                            textareaClassName="focus:outline-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1 text-foreground">Human Readable Answer</label>
                        <textarea
                          value={q.human_readable_answer || ''}
                          onChange={(e) => updateQuestion(index, 'human_readable_answer', e.target.value)}
                          className="w-full p-2 border rounded-lg text-sm bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                          rows={4}
                          placeholder="Enter the expected answer in human-readable format"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1 text-foreground">Explanation</label>
                        <textarea
                          value={q.explanation || ''}
                          onChange={(e) => updateQuestion(index, 'explanation', e.target.value)}
                          className="w-full p-2 border rounded-lg text-sm bg-background text-foreground border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary"
                          rows={3}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      <p>
                        {q.domain && <><strong className="text-foreground">Domain:</strong> {q.domain} | </>}
                        <strong className="text-foreground">Difficulty:</strong> {q.difficulty}
                      </p>
                      <p className="mt-1 text-foreground/80">{q.question}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Save Confirmation Modal */}
      {showSaveConfirmModal && editedDataset && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[300]">
          <div className="glass-modal max-w-md w-full mx-4 p-6">
            <h3 className="text-xl font-bold mb-4 text-foreground">
              Dataset Already Exists
            </h3>
            <p className="text-muted-foreground mb-2">
              Dataset <span className="font-bold text-primary">"{editedDataset.version}"</span> already exists.
            </p>
            <p className="text-muted-foreground mb-6">
              Do you want to overwrite it, or change the version name to create a new dataset?
            </p>
            <div className="flex gap-3">
              <button
                onClick={performSave}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-warning text-warning-foreground rounded-lg hover:bg-warning/80 disabled:opacity-50 font-medium transition-colors"
              >
                Overwrite
              </button>
              <button
                onClick={() => setShowSaveConfirmModal(false)}
                className="flex-1 px-4 py-2 bg-card/50 text-muted-foreground rounded-lg hover:text-foreground hover:bg-card border border-primary/20 font-medium transition-colors"
              >
                Change Version
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[300]">
          <div className="glass-modal max-w-md w-full mx-4 p-6">
            <h3 className="text-xl font-bold mb-4 text-destructive">
              Confirm Delete
            </h3>
            <p className="text-muted-foreground mb-2">
              {deleteTarget.type === 'dataset' ? (
                <>
                  Are you sure you want to delete dataset{' '}
                  <span className="font-bold text-destructive">"{deleteTarget.id}"</span>?
                </>
              ) : (
                <>
                  Are you sure you want to remove question{' '}
                  <span className="font-bold text-destructive">
                    {editedDataset?.questions[deleteTarget.id as number]?.id}
                  </span>?
                </>
              )}
            </p>
            <p className="text-muted-foreground/80 mb-6 text-sm">
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={performDelete}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/80 disabled:opacity-50 font-medium transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteTarget(null);
                }}
                className="flex-1 px-4 py-2 bg-card/50 text-muted-foreground rounded-lg hover:text-foreground hover:bg-card border border-primary/20 font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
